import { z } from 'zod';
import { PASSWORD_MIN_LENGTH } from './constants';

// Security validation schemas
export const emailSchema = z
  .string()
  .trim()
  .min(1, { message: "Email é obrigatório" })
  .email({ message: "Email inválido" })
  .max(255, { message: "Email deve ter no máximo 255 caracteres" });

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, { message: `Senha deve ter no mínimo ${PASSWORD_MIN_LENGTH} caracteres` })
  .max(128, { message: "Senha deve ter no máximo 128 caracteres" })
  .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/, {
    message: "Senha deve conter ao menos uma letra minúscula, uma maiúscula, um número e um símbolo"
  });

export const nameSchema = z
  .string()
  .trim()
  .min(1, { message: "Nome é obrigatório" })
  .max(100, { message: "Nome deve ter no máximo 100 caracteres" })
  .regex(/^[a-zA-ZÀ-ÿ\s'&-]+$/, {
    message: "Nome deve conter apenas letras, espaços, hífens, apostrofes e &"
  });

export const phoneSchema = z
  .string()
  .trim()
  .refine((val) => {
    if (!val) return true; // permite vazio
    const digits = val.replace(/\D/g, '');
    return digits.length === 10 || digits.length === 11;
  }, { message: "Telefone deve ter 10 ou 11 dígitos (DDD + número)" });

export const textFieldSchema = z
  .string()
  .trim()
  .max(1000, { message: "Texto deve ter no máximo 1000 caracteres" })
  .refine(val => !/<script|javascript:|data:/i.test(val), {
    message: "Conteúdo não permitido detectado"
  });

// Sanitization functions
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
};

export const normalizePhone = (input: string): string => {
  const digits = input.replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return input;
};

// CPF/CNPJ validation
export const validateCPF = (cpf: string): boolean => {
  const cleanCPF = cpf.replace(/\D/g, '');
  if (cleanCPF.length !== 11 || /^(\d)\1{10}$/.test(cleanCPF)) return false;
  
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  let remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(10))) return false;
  
  return true;
};

export const validateCNPJ = (cnpj: string): boolean => {
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  if (cleanCNPJ.length !== 14 || /^(\d)\1{13}$/.test(cleanCNPJ)) return false;
  
  let sum = 0;
  let weight = 2;
  for (let i = 11; i >= 0; i--) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  let remainder = sum % 11;
  if (remainder < 2) remainder = 0;
  else remainder = 11 - remainder;
  if (remainder !== parseInt(cleanCNPJ.charAt(12))) return false;
  
  sum = 0;
  weight = 2;
  for (let i = 12; i >= 0; i--) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  remainder = sum % 11;
  if (remainder < 2) remainder = 0;
  else remainder = 11 - remainder;
  if (remainder !== parseInt(cleanCNPJ.charAt(13))) return false;
  
  return true;
};

export const cpfCnpjSchema = z
  .string()
  .trim()
  .min(1, { message: "CPF/CNPJ é obrigatório" })
  .refine((val) => {
    const digits = val.replace(/\D/g, '');
    return digits.length === 11 || digits.length === 14;
  }, { message: "CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos" })
  .refine((val) => {
    const digits = val.replace(/\D/g, '');
    if (digits.length === 11) return validateCPF(digits);
    if (digits.length === 14) return validateCNPJ(digits);
    return false;
  }, { message: "CPF ou CNPJ inválido" });

// Newsletter validation schema
export const newsletterSchema = z.object({
  email: emailSchema,
});

// Lead validation schema
export const leadSchema = z.object({
  nome: nameSchema,
  email: emailSchema.optional().or(z.literal('')),
  telefone: phoneSchema.optional(),
  evento: z.string().trim().min(1, { message: "Tipo de evento é obrigatório" }).max(200),
  valor_estimado: z.number().min(0, { message: "Valor deve ser positivo" }),
  origem: z.string().trim().max(50).optional(),
  observacoes: textFieldSchema.optional(),
  data_proxima_acao: z.string().optional().or(z.date())
});

// Client validation schema
export const clienteSchema = z.object({
  nome: nameSchema,
  email: emailSchema.optional().or(z.literal('')),
  telefone: phoneSchema.optional(),
  telefone_secundario: phoneSchema.optional(),
  cpf_cnpj: z.string().trim().max(20).optional(),
  rg: z.string().trim().max(20).optional(),
  estado_civil: z.enum(['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União estável']).optional().or(z.literal('')),
  endereco_rua: z.string().trim().max(200).optional(),
  endereco_numero: z.string().trim().max(10).optional(),
  endereco_complemento: z.string().trim().max(100).optional(),
  endereco_bairro: z.string().trim().max(100).optional(),
  endereco_cidade: z.string().trim().max(100).optional(),
  endereco_estado: z.string().trim().max(2).optional(),
  endereco_cep: z.string().trim().optional().or(z.literal('')).refine((val) => {
    if (!val) return true;
    const digits = val.replace(/\D/g, '');
    return digits.length === 8;
  }, { message: "CEP deve ter 8 dígitos" }),
  observacoes: textFieldSchema.optional(),
  preferencias_evento: textFieldSchema.optional()
});

// Client portal access validation
export const clientAccessSchema = z.object({
  token: z.string()
    .uuid('Token inválido')
    .min(1, 'Token é obrigatório'),
});

export type NewsletterInput = z.infer<typeof newsletterSchema>;
export type LeadInput = z.infer<typeof leadSchema>;
export type ClientAccessInput = z.infer<typeof clientAccessSchema>;